import { useEffect, useRef, useState } from 'react';
import { Triple } from '../../shared/types/triple.mts';
import { GuessEvent } from '../../shared/types/guess_event.mts';
import 'driver.js/dist/driver.css'; // Before Game.css so it has lower precedence.
import './Game.css';
import Checkmark from './assets/check_circle.svg';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { formatTimeElapsed, mod, logConversion, getStyleValue, runningAsNativeApp } from './utils.ts';
import { Carousel, CarouselItem } from './Carousel.tsx';
import {
    Guess,
    GuessTile,
    GuessLetterState,
    GameState,
    scoreToCssClass,
    TurnType,
    DifficultyLevel,
    SelectedPuzzle,
    BonusSelector,
    isCompleted,
    SpecialSelector,
    PuzzleState,
} from './State.tsx';
import { wordList } from './WordList.tsx';
import { track } from './umami.ts';
import { TOTAL_TURNS, TUTORIAL_ID } from './Constants.tsx';
import { CloseButton } from './CloseButton.tsx';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { usePuzzleIndex } from './PuzzleIndex.tsx';
import { useSubscriptionContext } from './SubscriptionContext.tsx';
import { useAdInterfaceContext } from './AdInterface.tsx';
import * as Sentry from '@sentry/capacitor';

const API_BASE_URL = window.API_BASE_URL;

export default function Puzzle({
    isFirstVisit,
    activeTriplitId,
    activeUserId,
    selector,
    sessionId,
    difficultyLevel,
    modalIsOpen,
    onWin,
    onLoss,
    onAlreadyCompleted,
    timerEnabled,
    onTimeElapsed,
    onCarouselTap,
    onFirstTutorialGuessFilled,
    onIncorrectTurn,
    tutorialOnScreen,
    onGameStateChanged,
    onGameDifficultyLevelChanged,
    onTutorialExited,
}: {
    isFirstVisit: boolean;
    activeTriplitId: string;
    activeUserId: string | undefined;
    selector: SelectedPuzzle;
    sessionId: string;
    difficultyLevel: DifficultyLevel;
    modalIsOpen: boolean;
    onWin: (score: number, shareData: ShareData) => void;
    onLoss: (score: number, shareData: ShareData) => void;
    onAlreadyCompleted: (score: number, shareData: ShareData) => void;
    timerEnabled: boolean;
    onTimeElapsed: (timeElapsed: number) => void;
    onCarouselTap: (tapCount: number) => void;
    onFirstTutorialGuessFilled: () => void;
    onIncorrectTurn: (turnType: TurnType, partialSubmit: boolean) => void;
    tutorialOnScreen: boolean;
    onGameDifficultyLevelChanged: (difficultyLevel: DifficultyLevel) => void;
    onGameStateChanged: (gameState: GameState) => void;
    onTutorialExited: () => void;
}) {
    // Context
    const { puzzleIndex, syncVersion } = usePuzzleIndex();
    const subscriptionContext = useSubscriptionContext();
    const adInterfaceContext = useAdInterfaceContext();

    // Prop-derived state
    const getPuzzleState = () => puzzleIndex.getPuzzleStateByIdOrThrow(activeTriplitId, activeUserId);

    // Persisted puzzle state
    const [gameState, setGameState] = useState(getInitialGameState);
    const [gameDifficultyLevel, setGameDifficultyLevel] = useState<DifficultyLevel>(getInitialGameDifficultyLevel);
    const [triple, setTriple] = useState<Triple>(getInitialTriple);
    const [guesses, setGuesses] = useState<Array<Guess>>(getInitialGuesses);
    const [timeElapsed, setTimeElapsed] = useState<number>(getInitialTimeElapsed);
    const [currentRow, setCurrentRow] = useState<number>(getInitialCurrentRow);
    const [currentCarouselIdx, setCurrentCarouselIdx] = useState<number>(getInitialCarouselIdx);
    const [cluesSynced, setCluesSynced] = useState(getInitialCluesSynced);
    const [attemptIdx, setAttemptIdx] = useState<number>(getInitialAttemptIdx);
    const [startTime, setStartTime] = useState<number>(getInitialStartTime);
    const [letterPermutation, setLetterPermutation] = useState<Array<number>>(getInitialLetterPermutation);
    const [score, setScore] = useState<number>(getInitialScore);
    const [solveOrder, setSolveOrder] = useState<Array<number>>(getInitialSolveOrder);
    const [lastSyncedTriplitId, setLastSyncedTriplitId] = useState<string>(activeTriplitId);
    const [lastSyncedUserId, setLastSyncedUserId] = useState<string | undefined>(activeUserId);

    // Add all persisted state variables to this array
    const PERSISTED_PUZZLE_STATE = [
        gameState,
        triple,
        guesses,
        currentRow,
        currentCarouselIdx,
        cluesSynced,
        attemptIdx,
        startTime,
        gameDifficultyLevel,
        letterPermutation,
        score,
        timeElapsed,
    ];

    function tutorialMode() {
        return activeTriplitId === TUTORIAL_ID;
    }

    function getInitialGameState() {
        const puzzleState = getPuzzleState();
        if (!puzzleState) {
            return GameState.IN_PROGRESS;
        }
        return puzzleState.gameState || GameState.IN_PROGRESS;
    }

    function getInitialGameDifficultyLevel() {
        const puzzleState = getPuzzleState();
        if (tutorialMode()) {
            return DifficultyLevel.NORMAL;
        }
        return puzzleState?.gameDifficultyLevel != null ? puzzleState.gameDifficultyLevel : difficultyLevel;
    }

    function getInitialTriple(): Triple {
        const puzzleState = getPuzzleState();
        if (getInitialCluesSynced()) {
            return puzzleState.triple.copyWithOrderedEntries();
        } else {
            return puzzleState.triple;
        }
    }

    function getInitialGuesses() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.guesses) {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            return puzzleState.guesses.map(Guess.from);
        } else {
            return [new Guess(4), new Guess(5), new Guess(6)];
        }
    }

    function getInitialTimeElapsed() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.timeElapsed != null) {
            return puzzleState.timeElapsed;
        } else {
            return 0;
        }
    }

    function getInitialCurrentRow() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.currentRow != null) {
            return puzzleState.currentRow;
        } else {
            return getInitialGameDifficultyLevel() === DifficultyLevel.EASY ? 0 : -1;
        }
    }

    function getInitialCarouselIdx() {
        const puzzleState = getPuzzleState();
        if (!getInitialCluesSynced() && puzzleState?.currentCarouselIdx != null) {
            return puzzleState.currentCarouselIdx;
        } else {
            return getInitialCluesSynced() ? toCarouselIndex(getInitialCurrentRow()) : 0;
        }
    }

    function getInitialCluesSynced() {
        const puzzleState = getPuzzleState();
        if (isCompleted(puzzleState?.gameState)) {
            return true;
        }
        if (puzzleState?.cluesSynced != null) {
            return puzzleState.cluesSynced;
        }
        return getInitialGameDifficultyLevel() === DifficultyLevel.EASY;
    }

    function getInitialAttemptIdx() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.attemptIdx != null) {
            return puzzleState.attemptIdx;
        }
        return 0;
    }

    function getInitialStartTime() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.startTime != null) {
            return puzzleState.startTime;
        }
        return new Date().getTime();
    }

    function getInitialScore() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.score != null) {
            return puzzleState.score;
        }
        return -1;
    }

    function getInitialLetterPermutation() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.letterPermutation != null) {
            return puzzleState.letterPermutation;
        }
        return [0, 1, 2];
    }

    function getInitialSolveOrder() {
        const puzzleState = getPuzzleState();
        if (puzzleState?.solveOrder != null) {
            return puzzleState.solveOrder;
        }
        return [];
    }

    // Non-persisted state
    const [emptyBoardErrorVisible, setEmptyBoardErrorVisible] = useState(false);
    const [partiallyFilledRowsErrorVisible, setPartiallyFilledRowsErrorVisible] = useState(false);
    const [noUpdateErrorVisible, setNoUpdateErrorVisible] = useState(false);
    const [invalidGuessErrorVisible, setInvalidGuessErrorVisible] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [notifyTurnsRemaining, setNotifyTurnsRemaining] = useState(false);
    const [hasFocus, setHasFocus] = useState(true);
    const [tilesFinalized, setTilesFinalized] = useState(getInitialTilesFinalized);
    const [animationComplete, setAnimationComplete] = useState(getInitialAnimationComplete);
    const [firstTutorialGuessFilled, setFirstTutorialGuessFilled] = useState(false);
    const [numNonTourCarouselTaps, setNumNonTourCarouselTaps] = useState<number>(0);

    function getInitialTilesFinalized() {
        const puzzleState = getPuzzleState();
        return isCompleted(puzzleState?.gameState);
    }

    function getInitialAnimationComplete() {
        const puzzleState = getPuzzleState();
        return isCompleted(puzzleState?.gameState);
    }

    // Synced refs (refs synced with state; these are read-only)
    const tripleRef = useRef(triple);
    useEffect(() => {
        tripleRef.current = triple;
    }, [triple]);

    // Refs
    const guessContainerRef = useRef(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    function startTimer() {
        if (timerRef.current == null) {
            timerRef.current = setInterval(() => {
                setTimeElapsed((te) => te + 1);
            }, 1000);
        }
    }

    function stopTimer() {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }

    useEffect(() => {
        if (gameState === GameState.IN_PROGRESS && !modalIsOpen && hasFocus && !tutorialMode()) {
            startTimer();
        } else {
            stopTimer();
        }
    }, [gameState, modalIsOpen, hasFocus, activeTriplitId, activeUserId]);

    useEffect(() => {
        onGameStateChanged(gameState);
    }, [onGameStateChanged, gameState]);

    function getBuildHash() {
        // Somewhat fragile, but it at least does not depend on the order of script tags, and the name format
        // should be pretty stable over time.
        const scripts = document.getElementsByTagName('script');
        return (
            Array.from(scripts)
                ?.find((script) => script.src.includes('/static/main'))
                ?.src.split('/')
                ?.pop()
                ?.split('.')?.[1] ?? 'unknown'
        );
    }

    function sendInstrumentation(
        allCorrect: boolean,
        attemptIdx: number,
        turnType: TurnType,
        updatedGuesses: Guess[],
        updatedGameState: GameState,
    ): void {
        fetch(`${API_BASE_URL}/api/v1/guess`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(
                GuessEvent.from({
                    userAgent: navigator.userAgent,
                    tripleId: triple.id,
                    attemptIdx: attemptIdx,
                    guess0: updatedGuesses[0].toStringExcludeFailures(),
                    guess1: updatedGuesses[1].toStringExcludeFailures(),
                    guess2: updatedGuesses[2].toStringExcludeFailures(),
                    correct: allCorrect,
                    duration: timeElapsed,
                    submitTime: new Date(),
                    build: getBuildHash(),
                    clientVersion: window.CLIENT_VERSION,
                    difficultyLevel: gameDifficultyLevel,
                    turnType: turnType,
                    sessionId: sessionId,
                    newUser: isFirstVisit,
                    timerEnabled: timerEnabled,
                    gameState: GameState[updatedGameState],
                    solveOrder: JSON.stringify(solveOrder),
                }),
            ),
        }).catch((e) => {
            console.log('Error sending instrumentation', e);
        });
    }

    function flashNotification(setter: (arg: boolean) => void, duration: number) {
        setter(true);
        setTimeout(() => {
            setter(false);
        }, duration);
    }

    function getCustomEventData(data = {}) {
        return Object.assign(
            {
                newUser: isFirstVisit,
            },
            data,
        );
    }

    function recordEvent(eventName) {
        track(`${tutorialMode() ? 'tutorial_' : ''}${eventName}`, getCustomEventData());
    }

    function giveHapticFeedback(): Promise<void> {
        if (runningAsNativeApp()) {
            return Haptics.impact({ style: ImpactStyle.Heavy });
        } else {
            return Promise.resolve();
        }
    }

    function handleTurn(turnType: TurnType) {
        if (isCompleted(gameState)) {
            return;
        }
        let updatedGuesses = guesses;
        switch (turnType) {
            case TurnType.REVEAL:
                if (currentRow === -1) {
                    updateCurrentRow(0);
                }
                updatedGuesses = revealNextLetter(currentRow);
                break;
            case TurnType.SUBMIT: {
                const allEmpty = guesses.every((guess) => {
                    return guess.empty();
                });
                if (allEmpty) {
                    flashNotification(setEmptyBoardErrorVisible, 2000);
                    return;
                }
                const somePartiallyFilled = guesses.some((guess) => {
                    return guess.partial();
                });
                if (somePartiallyFilled) {
                    flashNotification(setPartiallyFilledRowsErrorVisible, 2000);
                    return;
                }
                const checkable = guesses.some((guess) => {
                    return guess.checkable();
                });
                if (!checkable) {
                    flashNotification(setNoUpdateErrorVisible, 2000);
                    return;
                }
                const allValidWords = guesses
                    .filter((guess) => guess.full())
                    .every((guess, idx) => {
                        return wordList.has(guess.toString());
                    });
                if (!allValidWords) {
                    flashNotification(setInvalidGuessErrorVisible, 2000);
                    return;
                }
                updatedGuesses = checkAllGuesses();
                break;
            }
            default:
                throw new Error(`Unsupported turnType: ${turnType}`);
        }
        setGuesses(updatedGuesses);
        const turn = attemptIdx + 1;
        const allCorrect = updatedGuesses.every((guess) => {
            return guess.verified();
        });
        let updatedGameState: GameState;
        if (allCorrect || turn === TOTAL_TURNS) {
            void adInterfaceContext.prepareCompletionInterstitialAdIfNeeded().catch((e) => {
                console.error('[Puzzle] Error preparing interstitial ad:', e);
                Sentry.captureException(e);
            });
            const oldTriple = new Triple(triple);
            const completionCallback = allCorrect ? onWin : onLoss;
            updatedGameState = allCorrect ? GameState.WON : GameState.LOST;
            setGameState(updatedGameState);
            const phaseDuration =
                2 *
                parseInt(
                    /([\d]+)ms/.exec(
                        window
                            .getComputedStyle(document.documentElement)
                            .getPropertyValue('--turn-transition-duration')
                            .trim(),
                    )[0],
                    10,
                );
            const callbacks: Array<() => void> = [];
            let updatedScore: number;

            // User has successfully completed the tutorial for the first time.
            // We count this as a conversion if the user came via an ad.
            if (isFirstVisit && tutorialMode()) {
                logConversion();
            }
            if (allCorrect) {
                updatedScore = attemptIdx + 1;
                recordEvent('submission_correct');
                void giveHapticFeedback();
            } else {
                updatedScore = -1;
                setAttemptIdx((attemptIdx) => attemptIdx + 1);
                // Reveal tiles that were never correctly guessed
                setGuesses(
                    updatedGuesses.map((guess) => {
                        return guess.markFailures();
                    }),
                );
                setFadeOut(true);
                // No turns remaining. This is loss.
                recordEvent('failure');
                callbacks.push(() => {
                    // Check to make sure the triple is still valid and that the user hasn't navigated away to a new puzzle.
                    if (oldTriple.id === tripleRef.current.id) {
                        setGuesses(
                            updatedGuesses.map((guess, idx) => {
                                return guess.revealDueToFailure(triple.getOrderedEntries()[idx].answer);
                            }),
                        );
                        setFadeIn(true);
                    }
                });
            }
            callbacks.push(() => {
                if (oldTriple.id === tripleRef.current.id) {
                    setTilesFinalized(true);
                }
            });
            setScore(updatedScore);
            for (let i = 0; i < callbacks.length; i++) {
                setTimeout(
                    () => {
                        callbacks[i]();
                    },
                    (i + 1) * phaseDuration,
                );
            }
            const animationDuration = (callbacks.length + 1) * phaseDuration;
            setTimeout(() => {
                if (oldTriple.id === tripleRef.current.id) {
                    setAnimationComplete(true);
                }
                void generateShareData({
                    gameState: updatedGameState,
                    puzzleNumber: triple.offset + 1,
                    score: turn,
                    timeElapsed,
                    updatedGuesses,
                    gameDifficultyLevel,
                }).then((blob) => {
                    completionCallback(updatedScore, blob);
                });
            }, animationDuration);
            setTimeout(() => {
                if (oldTriple.id === tripleRef.current.id) {
                    orderCluesAndSetTriple(oldTriple);
                }
            }, animationDuration + 2000);
            sendInstrumentation(allCorrect, attemptIdx + 1, turnType, updatedGuesses, updatedGameState);
        } else {
            updatedGameState = GameState.IN_PROGRESS;
            flashNotification(setNotifyTurnsRemaining, 2000);
            setAttemptIdx((attemptIdx) => attemptIdx + 1);
            sendInstrumentation(/* allCorrect= */ false, attemptIdx + 1, turnType, updatedGuesses, updatedGameState);
            const partialSubmit = turnType === TurnType.SUBMIT && updatedGuesses.some((guess) => guess.empty());
            setTimeout(() => onIncorrectTurn(turnType, partialSubmit), 1000);
            switch (turnType) {
                case TurnType.REVEAL:
                    recordEvent('reveal');
                    break;
                case TurnType.SUBMIT:
                    recordEvent('submission_incorrect');
                    break;
            }
        }
    }

    async function generateShareData({
        gameState,
        puzzleNumber,
        score,
        timeElapsed,
        updatedGuesses,
        gameDifficultyLevel,
    }: {
        gameState: GameState;
        puzzleNumber: number;
        score: number;
        timeElapsed: number;
        updatedGuesses: Guess[];
        gameDifficultyLevel: DifficultyLevel;
    }) {
        const width = 1200;
        const height = 630;
        const scale = 2;
        const fallbackFonts = "'Segoe UI', 'Helvetica Neue', 'Roboto', sans-serif";
        const titleFontStack = `'Quicksand', ${fallbackFonts}`;
        // Ensure fonts are loaded for rasterization
        const titleFont = `700 95px ${titleFontStack}`;
        const subTitleFont = `400 80px ${titleFontStack}`;
        try {
            await Promise.all([document.fonts.load(titleFont), document.fonts.load(subTitleFont)]);
        } catch (error) {
            console.warn('Error loading fonts:', error);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);

        // Define colors from 1 (fast) to 5 (slow)
        // We use the light mode version of the colors because the background of the image will be white.
        const COLORS = {
            1: getStyleValue('--first-turn-color-light'),
            2: getStyleValue('--second-turn-color-light'),
            3: getStyleValue('--third-turn-color-light'),
            4: getStyleValue('--fourth-turn-color-light'),
            5: getStyleValue('--fifth-turn-color-light'),
        };

        const yOffset = height / 5.5;

        // Clear canvas
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);

        // Draw header text
        ctx.fillStyle = '#000';
        ctx.font = titleFont;
        ctx.textAlign = 'center';
        let title;
        if (selector instanceof BonusSelector) {
            title = `Triplit Bonus ${selector.id}`;
        } else if (selector instanceof SpecialSelector) {
            title = `Triplit Special ${puzzleNumber}`;
        } else {
            title = `Triplit ${puzzleNumber}`;
        }
        ctx.fillText(title, width / 2, yOffset);

        ctx.font = subTitleFont;
        ctx.fillText(
            `${score}/5 ${formatTimeElapsed(timeElapsed)} ${toProLevelEmoji()}${toTimeBadgeEmoji(
                timeElapsed,
            )}${toDifficultyLevelEmoji(gameDifficultyLevel)}${toSolveOrderEmoji()}`,
            width / 2,
            100 + yOffset,
        );

        // Draw pyramid blocks with rounded corners
        const boxSize = 100;
        const padding = 10;
        const radius = 8;
        const startY = 160 + yOffset;

        updatedGuesses.forEach((guess: Guess, rIdx) => {
            const rowWidth = guess.tiles.length * (boxSize + padding) - padding;
            const offsetX = (width - rowWidth) / 2;
            const y = startY + rIdx * (boxSize + padding);

            guess.tiles.forEach((tile, cIdx) => {
                const x = offsetX + cIdx * (boxSize + padding);
                ctx.fillStyle = COLORS[tile.turnSolved] || '#ccc';

                // Rounded rectangle
                ctx.beginPath();
                ctx.moveTo(x + radius, y);
                ctx.lineTo(x + boxSize - radius, y);
                ctx.quadraticCurveTo(x + boxSize, y, x + boxSize, y + radius);
                ctx.lineTo(x + boxSize, y + boxSize - radius);
                ctx.quadraticCurveTo(x + boxSize, y + boxSize, x + boxSize - radius, y + boxSize);
                ctx.lineTo(x + radius, y + boxSize);
                ctx.quadraticCurveTo(x, y + boxSize, x, y + boxSize - radius);
                ctx.lineTo(x, y + radius);
                ctx.quadraticCurveTo(x, y, x + radius, y);
                ctx.closePath();
                ctx.fill();
            });
        });

        const blob = await canvasToBlob(canvas);
        const file = new File([blob], 'triplit-share.png', { type: blob.type });
        let link = `https://triplit.com${selector.targetPath()}`;
        // Remove query parameters from the link
        link = link.split('?')[0];
        // Remove trailing slash if present
        link = link.charAt(link.length - 1) === '/' ? link.slice(0, -1) : link;
        let shareText: string;
        switch (gameState) {
            case GameState.WON:
                shareText = `I cracked it. Can you?`;
                break;
            case GameState.LOST:
                shareText = `I couldn't quite crack it. Can you?`;
                break;
            default:
                shareText = `I'm playing Triplit ${puzzleNumber}. Can you crack it?`;
        }
        const shareData: ShareData = {
            files: [file],
            title: title,
            text: `${title}: ${shareText} ${link}.`,
        };

        return shareData;
    }

    function toDifficultyLevelEmoji(difficultyLevel: DifficultyLevel) {
        switch (difficultyLevel) {
            case DifficultyLevel.EASY:
                return '🐣';
            case DifficultyLevel.HARD:
                return '🐦‍🔥';
            default:
                return '🐦';
        }
    }

    function toTimeBadgeEmoji(timeElapsed: number) {
        if (timeElapsed < 30) {
            return '⚡';
        } else if (timeElapsed > 300) {
            return '🐢';
        } else {
            return '';
        }
    }

    function toProLevelEmoji() {
        if (subscriptionContext.pro()) {
            return '💎';
        } else {
            return '';
        }
    }

    function toSolveOrderEmoji() {
        if (solveOrder.length === 3 && [0, 1, 2].every((val, idx) => val === solveOrder[idx])) {
            return '🪜';
        } else if (solveOrder.length === 3 && [2, 1, 0].every((val, idx) => val === solveOrder[idx])) {
            return '🛝';
        } else {
            return '';
        }
    }

    function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Canvas toBlob() failed'));
                }
            });
        });
    }

    function handleMoveUp() {
        if (currentRow === -1) {
            updateCurrentRow(2);
            return;
        }
        // Simply move the row forward by one
        updateCurrentRow(mod(currentRow - 1, 3));
    }

    function handleMoveDown() {
        if (currentRow === -1) {
            updateCurrentRow(0);
            return;
        }
        // Simply move the row forward by one
        updateCurrentRow(mod(currentRow + 1, 3));
    }

    function handleKeyboardPress(button: string) {
        if (modalIsOpen) {
            console.info(`Puzzle: Modal is open, ignoring keyboard press of button ${button}`);
            return;
        }
        let row = currentRow;
        if (currentRow === -1) {
            updateCurrentRow(0);
            row = 0;
        }
        if (button === '{bksp}' || button === '{backspace}' || button === '{delete}') {
            deleteLastLetter(row);
        } else if (button === '{clear}') {
            clearGuess(row);
        } else if (button.match(/^[A-Za-z]$/)) {
            appendLetter(button.toLowerCase(), row);
        }
    }

    /* This function intercepts key presses for keys which are NOT included in the on-screen keyboard. */
    // prettier-ignore
    function handleSpecialKeys(e: KeyboardEvent) { // eslint-disable-line react-hooks/exhaustive-deps
        if (modalIsOpen) {
            console.info(`Puzzle: Modal is open, ignoring special key press of key ${e.key}`);
            return;
        }
        if ((e.shiftKey || e.getModifierState('CapsLock')) && e.key.match(/^[A-Za-z]$/)) {
            handleKeyboardPress(e.key);
            // Stop propagation to ensure that the key is not registered twice
            e.stopPropagation();
            return;
        } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
            handleMoveDown();
        } else if (e.key === 'ArrowUp') {
            handleMoveUp();
        }
    }

    function deleteLastLetter(guessIdx: number) {
        if (isCompleted(gameState)) {
            return;
        }
        const new_guesses = guesses.map((guess, i) => {
            return i === guessIdx ? guess.deleteLastLetter() : guess.copy();
        });
        setGuesses(new_guesses);
    }

    function isGuessCorrect(guess: Guess, guessIdx: number): boolean {
        return triple.getOrderedEntries()[guessIdx].answer === guess.toString();
    }

    function maybeUpdateSolveOrder(guess: Guess, guessIdx: number) {
        if (isGuessCorrect(guess, guessIdx)) {
            setSolveOrder((prevSolveOrder) => {
                // If it is, and it has not been entered before, add it to the solve order
                if (!prevSolveOrder.includes(guessIdx)) {
                    return [...prevSolveOrder, guessIdx];
                } else {
                    return prevSolveOrder;
                }
            });
        }
    }

    function appendLetter(newLetter: string, guessIdx: number) {
        if (isCompleted(gameState)) {
            return;
        }
        // Normalize letter to lower case
        newLetter = newLetter.toLowerCase();
        // Remember, react state is supposed to be immutable, so we need to create a new
        // array here.
        const newGuesses = guesses.map((guess, i) => {
            if (i === guessIdx) {
                const updatedGuess = guess.append(newLetter);
                if (tutorialMode() && updatedGuess.full() && !firstTutorialGuessFilled) {
                    setFirstTutorialGuessFilled(true);
                    onFirstTutorialGuessFilled();
                }
                maybeUpdateSolveOrder(updatedGuess, guessIdx);
                return updatedGuess;
            } else {
                return guess;
            }
        });
        setGuesses(newGuesses);
    }

    function clearGuess(idx: number) {
        if (isCompleted(gameState)) {
            return;
        }
        setGuesses((prevGuesses) => {
            return prevGuesses.map((guess, i) => {
                if (i === idx) {
                    return guess.clear();
                } else {
                    return guess;
                }
            });
        });
    }

    function checkGuess(indices: number[]): Array<Guess> {
        if (isCompleted(gameState)) {
            return guesses;
        }
        const newGuesses = guesses.map((guess, i) => {
            if (!indices.includes(i) || !guess.checkable()) {
                return guess.copy();
            }
            const answer = triple.getEntryByLength(guess.length()).answer;
            return new Guess(
                guess.tiles.map((tile, j) => {
                    if (tile.turnSolved > -1) {
                        return tile;
                    }
                    if (tile.char === answer[j]) {
                        return new GuessTile(tile.char, GuessLetterState.CORRECT, attemptIdx + 1);
                    } else {
                        return new GuessTile(tile.char, GuessLetterState.INCORRECT);
                    }
                }),
            );
        });

        return newGuesses;
    }

    function checkAllGuesses(): Array<Guess> {
        if (isCompleted(gameState)) {
            return guesses;
        }
        return checkGuess([0, 1, 2]);
    }

    function revealNextLetter(idx: number): Array<Guess> {
        if (isCompleted(gameState)) {
            return guesses;
        }
        idx = Math.max(idx, 0);
        if (!guesses[idx].editable()) {
            // no-op
            return guesses;
        }
        const newGuesses = triple.getOrderedEntries().map((entry, i) => {
            if (i === idx) {
                const newGuess = guesses[i].revealNextLetter(entry.answer, attemptIdx + 1);
                maybeUpdateSolveOrder(newGuess, i);
                return newGuess;
            }
            return guesses[i].copy();
        });

        return newGuesses;
    }

    // This adjustment is necessary because the zeroth element of the carousel
    // is a one-time element.
    function toCarouselIndex(rowIdx: number) {
        return rowIdx;
    }

    // This adjustment is necessary because the zeroth element of the carousel
    // is a one-time element.
    function toRowIndex(carouselIdx: number) {
        return carouselIdx;
    }

    function onCarouselSwipe(newCarouselIdx: number) {
        if (!tutorialOnScreen) {
            // Do not count carousel taps when the tutorial is on screen
            setNumNonTourCarouselTaps((numCarouselTaps) => numCarouselTaps + 1);
        }
        setCurrentCarouselIdx(newCarouselIdx);
        if (cluesSynced) {
            setCurrentRow(toRowIndex(newCarouselIdx));
        }
    }

    function updateCurrentRow(idx: number, forceSyncCarousel = false) {
        setCurrentRow(idx);
        if (cluesSynced || forceSyncCarousel) {
            // This adjustment is necessary because the zeroth element of the carousel
            // is a one-time element.
            setCurrentCarouselIdx(toCarouselIndex(idx));
        }
    }

    useEffect(() => {
        window.addEventListener('keydown', handleSpecialKeys, { capture: true });
        return () => window.removeEventListener('keydown', handleSpecialKeys, { capture: true });
    }, [currentRow, handleSpecialKeys]);

    function writePuzzleState() {
        if (lastSyncedTriplitId !== activeTriplitId || lastSyncedUserId !== activeUserId) {
            // We have switched to a new puzzle or a new user, so we should not yet state
            return;
        }

        const existingPuzzleState = getPuzzleState();
        const updatedPuzzleState = new PuzzleState({
            guesses,
            gameState,
            cluesSynced,
            currentRow,
            currentCarouselIdx: toRowIndex(currentCarouselIdx),
            triple: existingPuzzleState.triple, // Always persist the triple in its original order
            attemptIdx,
            startTime,
            gameDifficultyLevel,
            sessionId,
            letterPermutation,
            score,
            solveOrder,
            timeElapsed,
        });
        void puzzleIndex.createOrUpdate(updatedPuzzleState, activeUserId);
    }

    // Persisted-state related effects
    useEffect(() => {
        if (!tutorialMode()) {
            writePuzzleState();
        }
    }, PERSISTED_PUZZLE_STATE);

    // Declare this AFTER the useEffect above to ensure we've flushed any local changes to the index
    // before we read from it and reinitialize.
    useEffect(() => {
        if (lastSyncedTriplitId === TUTORIAL_ID && activeTriplitId === TUTORIAL_ID) {
            // If we're in the tutorial and we've already initialized the state, don't reset it.
            // Tutorial state is not persisted or synced.
            return;
        }
        const gameState = getInitialGameState();
        setGameState(gameState);
        const score = getInitialScore();
        setScore(score);
        const gameDifficultyLevel = getInitialGameDifficultyLevel();
        setGameDifficultyLevel(gameDifficultyLevel);
        setCluesSynced(getInitialCluesSynced());
        const triple = getInitialTriple();
        setTriple(triple);
        const guesses = getInitialGuesses();
        setGuesses(guesses);
        const timeElapsed = getInitialTimeElapsed();
        setTimeElapsed(timeElapsed);
        if (isCompleted(gameState)) {
            void generateShareData({
                gameState,
                puzzleNumber: triple.offset + 1,
                score: score,
                timeElapsed,
                updatedGuesses: guesses,
                gameDifficultyLevel,
            }).then((blob) => {
                onAlreadyCompleted(score, blob);
            });
        }
        setCurrentRow(getInitialCurrentRow());
        setCurrentCarouselIdx(getInitialCarouselIdx());
        setAttemptIdx(getInitialAttemptIdx());
        setStartTime(getInitialStartTime());
        setLetterPermutation(getInitialLetterPermutation());
        setTilesFinalized(getInitialTilesFinalized());
        setAnimationComplete(getInitialAnimationComplete());
        setFadeIn(false);
        setFadeOut(false);
        setFirstTutorialGuessFilled(false);
        setSolveOrder(getInitialSolveOrder());

        // Guards to ensure we don't improperly write state before we've reacted to prop changes.
        setLastSyncedTriplitId(activeTriplitId);
        setLastSyncedUserId(activeUserId);
    }, [activeTriplitId, activeUserId, syncVersion]);

    useEffect(() => {
        onTimeElapsed(timeElapsed);
    }, [timeElapsed]);
    useEffect(() => {
        onCarouselTap(numNonTourCarouselTaps);
    }, [currentCarouselIdx]);

    useEffect(() => {
        const handleBlur = (e) => {
            setHasFocus(false);
        };
        const handleFocus = (e) => {
            setHasFocus(true);
        };
        const handleVisbilityChange = () => {
            if (document.visibilityState === 'hidden') {
                setHasFocus(false);
            } else if (document.visibilityState === 'visible') {
                setHasFocus(true);
            }
        };
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('visibilitychange', handleVisbilityChange);
        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('visibilitychange', handleVisbilityChange);
        };
    }, []);

    function permuteLetters() {
        const newPermutation = [...letterPermutation];
        // Ensure the permutation is different every time
        while (newPermutation.every((val, idx) => val === letterPermutation[idx])) {
            for (let i = triple.triplet.length - 1; i >= 0; i--) {
                // Choose j at random
                const j = Math.floor(Math.random() * (i + 1));
                // Swap
                [newPermutation[i], newPermutation[j]] = [newPermutation[j], newPermutation[i]];
            }
        }
        setLetterPermutation(newPermutation);
    }

    // This is our mechanism for allowing players to permanently downshift the difficulty level
    // from the settings menu and have the change take effect immediately.
    useEffect(() => {
        if (difficultyLevel < gameDifficultyLevel && !isCompleted(gameState)) {
            setGameDifficultyLevel(difficultyLevel);
            if (difficultyLevel === DifficultyLevel.EASY) {
                orderCluesAndSetTriple(triple);
            }
        }
    }, [difficultyLevel]);

    // Communicate changes in the game difficulty level to the parent component. This is useful for notifying
    // the player when changes can take effect immediately (when the player selects an easier difficulty level
    // in settings) vs. when the change will take effect on the next puzzle (when the player selects a harder difficulty level).
    useEffect(() => {
        onGameDifficultyLevelChanged(gameDifficultyLevel);
    }, [gameDifficultyLevel]);

    // When clues are ordered from the carousel, we do not permanently change the difficulty setting,
    // we just change it for this puzzle.
    function onOrderClues() {
        if (!confirm('Clues are currently in random order. Do you want to order the clues?')) {
            return;
        }
        track('order_clues_from_carousel', getCustomEventData());
        setGameDifficultyLevel(DifficultyLevel.EASY);
        orderCluesAndSetTriple(triple);
    }

    function orderCluesAndSetTriple(triple: Triple) {
        setTriple(Object.assign(new Triple(triple), { entries: triple.getOrderedEntries() }));
        setCluesSynced(true);
        const row = currentRow === -1 ? 0 : currentRow;
        updateCurrentRow(row, /* forceSyncCarousel= */ true);
    }

    function selectionDisabled() {
        return isCompleted(gameState) && score > -1 && !animationComplete;
    }

    return (
        <div className='inner-container'>
            <div className='game-container'>
                {tutorialMode() && <CloseButton className='tutorial-exit-button' onClose={onTutorialExited} />}
                <div className='turn-indicator-container' data-testid='turn-indicator-container'>
                    {new Array(TOTAL_TURNS).fill(null).map((_, idx) => {
                        return (
                            <div
                                key={idx}
                                className={`turn-indicator ${idx < attemptIdx ? 'turn-indicator-empty' : ''} ${
                                    tilesFinalized ? scoreToCssClass(score) : ''
                                } ${
                                    tilesFinalized
                                        ? scoreToCssClass(
                                              score,
                                              /* fill= */ false,
                                              /* outline=*/ false,
                                              /* border= */ true,
                                          )
                                        : ''
                                }`}
                            />
                        );
                    })}
                </div>
                <div className='guesses-container' ref={guessContainerRef}>
                    <div
                        className='guess-row triplet-container'
                        data-testid='triplit-letters'
                        style={{
                            display: gameDifficultyLevel === DifficultyLevel.HARD ? 'none' : '',
                        }}>
                        {triple.triplet.split('').map((triplitChar, letterIdx) => (
                            <div
                                className={`input-container cursor-pointer triplet-letter-container`}
                                key={letterIdx}
                                onClick={permuteLetters}
                                style={{
                                    transform: `translateX(calc(${
                                        (letterPermutation[letterIdx] - letterIdx) * 100
                                    }% + ${
                                        (letterPermutation[letterIdx] - letterIdx) *
                                        2 *
                                        parseFloat(getStyleValue('--letter-margin-rem'))
                                    }rem)`,
                                    transition: 'transform 0.5s',
                                }}>
                                <div id={`triplet`} className='input-box triplet-box'>
                                    {triplitChar}
                                </div>
                            </div>
                        ))}
                    </div>
                    {guesses.map((guess, guessIdx) => {
                        return (
                            <div
                                role='textbox'
                                aria-label={`${4 + guessIdx}-letter guess`}
                                onClick={(e) => updateCurrentRow(guessIdx)}
                                className={`guess-row ${
                                    guessIdx === 0 && gameDifficultyLevel === DifficultyLevel.HARD
                                        ? 'first-row-no-triplit'
                                        : ''
                                }`}
                                key={guessIdx}>
                                {guess.tiles.map((tile, letterIdx) => (
                                    <div
                                        className={`input-container cursor-pointer ${
                                            isCompleted(gameState) ? 'tile-completed' : ''
                                        } ${tile.verified() ? 'tile-verified' : ''} ${
                                            tilesFinalized ? scoreToCssClass(tile.turnSolved) : ''
                                        } ${
                                            tilesFinalized
                                                ? scoreToCssClass(
                                                      tile.turnSolved,
                                                      /* fill= */ false,
                                                      /* outline=*/ true,
                                                  )
                                                : ''
                                        } ${tile.state === GuessLetterState.INCORRECT ? 'incorrect-letter' : ''} ${
                                            tile.state === GuessLetterState.FAILED ? 'failed-letter' : ''
                                        } ${guessIdx === currentRow && !selectionDisabled() ? 'selected' : ''}`}
                                        key={letterIdx}>
                                        <div
                                            id={`input_${guessIdx}_${letterIdx}`}
                                            className={`input-box ${
                                                tile.state === GuessLetterState.INITIAL && !tile.empty()
                                                    ? 'filled-in-by-user'
                                                    : ''
                                            } ${tile.state === GuessLetterState.INCORRECT ? 'input-incorrect' : ''} 
                                                    ${tile.state === GuessLetterState.FAILED ? 'input-failed' : ''} ${
                                                        tile.state === GuessLetterState.FAILED && fadeIn
                                                            ? 'fade-in'
                                                            : ''
                                                    }
                                                    ${
                                                        tile.state === GuessLetterState.FAILED && fadeOut
                                                            ? 'fade-out'
                                                            : ''
                                                    }`}>
                                            {tile.char || ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
                <div className='outer-clue-container'>
                    <Carousel
                        newIndex={currentCarouselIdx}
                        syncedWithParent={cluesSynced}
                        gameState={gameState}
                        onSwipe={onCarouselSwipe}
                        disabled={false}
                        disableAdvancedCarouselOptions={tutorialOnScreen}
                        onOrderClues={onOrderClues}>
                        {triple.entries.map((entry) => {
                            return (
                                <CarouselItem key={entry.clue}>
                                    {[guesses.find((guess) => guess.verifiedAndSpellsWord(entry.answer))].map(
                                        (guess, guessIdx) => {
                                            return (
                                                <div key={guessIdx} className={`clue-container`}>
                                                    <div
                                                        className='clue-checkmark-container'
                                                        style={{ visibility: 'hidden' }}>
                                                        <Checkmark
                                                            className={`clue-checkmark ${
                                                                guess == null ? 'clue-checkmark-hidden' : ''
                                                            }`}
                                                        />
                                                    </div>
                                                    <div className='clue-text'>{entry.clue}</div>
                                                    <div
                                                        className={`clue-checkmark-container ${
                                                            guess == null
                                                                ? 'clue-checkmark-hidden'
                                                                : tilesFinalized
                                                                  ? scoreToCssClass(
                                                                        guess.turnSolved(),
                                                                        /* fill= */ true,
                                                                    )
                                                                  : ''
                                                        }`}
                                                        data-testid='clue-checkmark-container'>
                                                        <Checkmark className='clue-checkmark' />
                                                    </div>
                                                </div>
                                            );
                                        },
                                    )}
                                </CarouselItem>
                            );
                        })}
                    </Carousel>
                </div>
                <div className='control-row'>
                    <button
                        className='cursor-pointer game-button action-button'
                        disabled={
                            isCompleted(gameState) || (currentRow >= 0 && guesses[currentRow].numEditableTiles() === 0)
                        }
                        onClick={(e) => {
                            handleTurn(TurnType.REVEAL);
                        }}>
                        Reveal
                    </button>
                    <button
                        className='cursor-pointer game-button action-button'
                        disabled={isCompleted(gameState)}
                        onClick={(e) => {
                            handleTurn(TurnType.SUBMIT);
                        }}>
                        Submit
                    </button>
                </div>
            </div>
            <div className='keyboard-container'>
                <Keyboard
                    onKeyPress={handleKeyboardPress}
                    physicalKeyboardHighlight={true}
                    physicalKeyboardHighlightPress={true}
                    disableButtonHold={true}
                    theme='hg-theme-default custom-keyboard-theme'
                    buttonAttributes={[
                        {
                            attribute: 'role',
                            value: 'button',
                            buttons: 'q w e r t y u i o p a s d f g h j k l {clear} z x c v b n m {backspace}',
                        },
                    ]}
                    display={{
                        '{backspace}': '⌫',
                        '{clear}': 'clear',
                    }}
                    layout={{
                        default: ['q w e r t y u i o p', 'a s d f g h j k l', '{clear} z x c v b n m {backspace}'],
                    }}
                />
            </div>
            <div role='status' className={`banner-popup ${emptyBoardErrorVisible ? '' : 'none'}`}>
                Please guess at least one word before submitting!
            </div>
            <div role='status' className={`banner-popup ${partiallyFilledRowsErrorVisible ? '' : 'none'}`}>
                Please clear or complete partially filled in rows before submitting.
            </div>
            <div role='status' className={`banner-popup ${noUpdateErrorVisible ? '' : 'none'}`}>
                Please update at least one guess before submitting!
            </div>
            <div role='status' className={`banner-popup ${invalidGuessErrorVisible ? '' : 'none'}`}>
                One or more guesses are not valid words.
            </div>
            <div role='status' className={`banner-popup ${notifyTurnsRemaining ? '' : 'none'}`}>
                {TOTAL_TURNS - attemptIdx} turn{TOTAL_TURNS - attemptIdx > 1 ? 's' : ''} remaining
            </div>
        </div>
    );
}
